/**
 * Will contain data about the logged in user.
 */
import { extendObservable } from 'mobx'
import { extractResponse, extractError } from '../service/response.service'
import { api } from '../service/api.service'

const PROFILE_ENDPOINT = `/user/profile`
class User {
  constructor() {
    extendObservable(this, {
      token: undefined,
      user: null,
      notification: null,
      contracts: [],
      activeContract: null,
      get contractAddress() {
        if (!this.activeContract) {
          return null
        }
        return this.activeContract.address
      },
      userType: undefined,
      userEmail: null,
      mailError: null,
      haveContractToSign: false,
      showContractSignModal: false,
      showDobProvideModal: false,
      showDobProvideAlert: false,
      initialLoad: true,
      contractsToProvideDob: []
    })

    this.callbacks = []
  }

  initContractStore(store) {
    this.contractStore = store
  }

  async loadUser() {
    return await this.getProfile()
      .then(user => {
        this.setupUser(user)
        return user
      })
      .catch(err => {
        console.log(err)
        throw err
      })
  }

  setHaveContractToSign(user) {
    const scriveSignId = user?.scriveSignId || ''
    const isParty =
      user?.contractAssignedData?.tenantsDetails?.find(
        (tenant) => tenant?.scriveLink?.includes(scriveSignId)
      )
    const notSignedByParties = user?.contractAssignedData?.tenantsDetails?.filter((party) => (party?.partySigned === false && isParty?.email === party?.email)) || []
    const haveContractToSign = notSignedByParties?.length > 0 
    this.haveContractToSign = haveContractToSign
  }

  setShowContractSignModal() {
    this.showContractSignModal = (!localStorage.getItem('hide_contract_assign_modal') && this.haveContractToSign) ? true : false
  }

  async dobsToProvideHandler(user, data) {
    const contractsToProvideDob = []
    let hideDob = false

    if (data?.action === 'skip') {
      localStorage.setItem(`skipDob`, 'true')
      localStorage.removeItem(`forceDob`)
      if (data?.dontAskAgain) {
        api.post('/contract/update-hide-dob-modal', { contracts: data?.allContractNumbers })
        hideDob = true
      }
    }

    let forceDob = localStorage.getItem('forceDob')

    if (this.initialLoad) {
      localStorage.removeItem(`skipDob`)
      localStorage.removeItem(`skipAlert`)
    }
  
    for (const contract of user?.contracts ?? []) {
      if (contract?.hideDobModal) {
        hideDob = true
      }
      if (contract?.missingDob) {
        const existingPartner = contractsToProvideDob?.find(c => c.partnerId === contract.partnerId);

        if (existingPartner) {
          existingPartner.contracts.push({
            id: contract.id,
            contractNumber: contract.contractNumber,
          });
        } else {
          contractsToProvideDob?.push({
            partnerId: contract.partnerId,
            partnerName: contract.partnerName,
            contracts: [{
              id: contract.id,
              contractNumber: contract.contractNumber,
            }],
          });
        }
      }
    }
    if (contractsToProvideDob?.length > 0) {
      this.contractsToProvideDob = contractsToProvideDob
      const skipDob = localStorage.getItem(`skipDob`)
      const skipAlert = localStorage.getItem(`skipAlert`)
      this.showDobProvideAlert = skipAlert ? false : true
      this.showDobProvideModal = forceDob ? true : (!skipDob && !hideDob)
    }
    this.initialLoad = false
  }

  setupUser(user) {
    this.user = user
    this.notification = user.notification
    this.contracts = user.contracts || []
    this.dobsToProvideHandler(user)
    this.onUserChange()
    this.setHaveContractToSign(this.user)
    this.setShowContractSignModal()
  }

  /**
   * Method logs in the user and returns the user data
   * or throws an error with the message
   * of an error that occured while logging in.
   *
   * @param username
   * @param password
   *
   * @param  {String} username    User's username or email for login
   * @param  {String} password    User's password for login
   * @return {Promise}            Resolves to object containing user data and token
   */

  sendVerificationEmail = async (id) => {
    this.mailError = null
    try {
      const response = await api.post('/user/send-verification-email', { id })
      const responseData = extractResponse(response)
      this.mailError = 'RESEND_VERIFICATION_EMAIL_SUCCESS'
      return responseData
    } catch (err) {
      this.mailError = 'RESEND_VERIFICATION_EMAIL_ERROR'
      throw err
    }
  }

  postRedirectHandler = async (tokenClaims) => {
    try {
      const response = await api.post('/user/post-redirect', tokenClaims)
      const responseData = extractResponse(response)
      return responseData
    } catch (err) {
      throw err
    }
  }

  onUserChange = () => {
    this.callbacks.forEach(fn => fn(this.user))
  }

  addOnUserChangeHandler = callbackHandler => {
    this.callbacks.push(callbackHandler)
  }

  async getProfile() {
    return await api.get(PROFILE_ENDPOINT).then(response => {
      const userData = response.data
      return userData
    })
  }

  confirmNewsletterSubscription(id) {
    return api
      .post('/user/confirm-newsletter-subscription', { id })
      .then(response => {
        return extractResponse(response)
      })
      .catch(err => {
        throw new Error(extractError(err))
      })
  }

  getUser() {
    return this.user
  }

  async confirmRegistrationInvitation(code) {
    const res = await api.post('/user/confirm-registration-invitation', { code })
    return res.data
  }
}

export default new User()
